import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Download, Eye, X, Copy, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { getTodayIST, formatDateTime } from '../utils/dateUtils';
import { exportTransactionReport } from '../utils/reportExport';

const Payouts = () => {
  const { user } = useAuth();
  // Always show fees by default, unless explicitly disabled
  const showFees = user?.show_rates_to_merchant !== 0 && user?.show_rates_to_merchant !== false;

  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  // Only the latest request may update the list (older responses can arrive late)
  const latestRequest = useRef(0);
  const [exporting, setExporting] = useState(false);
  const invalidRange = filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo;

  // Search after typing pauses, so each keystroke doesn't hit the list rate limit
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== filters.search) {
        handleFilterChange('search', searchInput.trim());
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!invalidRange) {
      loadPayouts();
    }
  }, [filters, pagination.page]);

  const loadPayouts = async () => {
    const requestId = ++latestRequest.current;
    try {
      setLoading(true);
      // Backend expects from_date/to_date (YYYY-MM-DD, read as IST days)
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (filters.status) params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.dateFrom) params.from_date = filters.dateFrom;
      if (filters.dateTo) params.to_date = filters.dateTo;

      const response = await apiService.getPayoutList(params);
      if (requestId !== latestRequest.current) return;
      if (response.success) {
        setPayouts(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.meta?.pagination?.totalCount || 0,
          totalPages: response.meta?.pagination?.totalPages || 0
        }));
      }
    } catch (error) {
      console.error('Failed to load payouts:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to load payouts');
    } finally {
      if (requestId === latestRequest.current) {
        setLoading(false);
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      status: '',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'queued':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getApprovalStatusColor = (approval_status) => {
    switch (approval_status?.toLowerCase()) {
      case 'auto_approved':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'awaiting_approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'completed':
        return <CheckCircle size={16} className="inline mr-1" />;
      case 'pending':
      case 'processing':
        return <Clock size={16} className="inline mr-1" />;
      case 'failed':
      case 'cancelled':
        return <XCircle size={16} className="inline mr-1" />;
      default:
        return <AlertCircle size={16} className="inline mr-1" />;
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const exportData = async () => {
    if (invalidRange) {
      toast.error('From date must be before To date');
      return;
    }
    try {
      setExporting(true);
      await exportTransactionReport('payout', filters, getTodayIST());
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(error.response?.data?.error?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Payouts</h1>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={exportData}
            disabled={exporting}
            className="btn-secondary inline-flex items-center disabled:opacity-50"
          >
            <Download size={16} className="mr-2" />
            {exporting ? 'Exporting...' : 'Export'}
          </button>
          <Link to="/payouts/create" className="btn-primary inline-flex items-center">
            <Plus size={16} className="mr-2" />
            Create Payout
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Payout ID, Reference ID, UTR, account..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
              <option value="expired">Expired</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              max={getTodayIST()}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={filters.dateTo}
              max={getTodayIST()}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>

        {invalidRange && (
          <p className="mt-3 text-sm text-red-600">From date must be before To date</p>
        )}

        {(filters.status || filters.dateFrom || filters.dateTo || filters.search || searchInput) && (
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {loading ? 'Searching...' : `${pagination.total} results found`}
            </span>
            <button
              onClick={clearFilters}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Payouts Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payout ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                {showFees && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fee
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reference ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Beneficiary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  UTR
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={showFees ? 9 : 8} className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                      <span className="ml-2">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : payouts.length > 0 ? (
                payouts.map((payout) => (
                  <tr key={payout.payout_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">
                          {payout.payout_id}
                        </span>
                        <button
                          onClick={() => copyToClipboard(payout.payout_id)}
                          className="ml-2 text-gray-400 hover:text-gray-600"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(payout.amount)}
                    </td>
                    {showFees && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatCurrency(payout.fee || 0)}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payout.reference_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{payout.beneficiary_name}</div>
                      <div className="text-sm text-gray-500">
                        {payout.beneficiary_vpa || payout.beneficiary_account || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payout.status)}`}>
                        {getStatusIcon(payout.status)}
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {payout.utr_number || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(payout.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedPayout(payout)}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={showFees ? 9 : 8} className="px-6 py-4 text-center text-gray-500">
                    No payouts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} results
            </div>
            <div className="flex space-x-1">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payout Details Modal */}
      {selectedPayout && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Payout Details
              </h3>
              <button
                onClick={() => setSelectedPayout(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Payout ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedPayout.payout_id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Amount</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatCurrency(selectedPayout.amount)}</dd>
                </div>
                {showFees && (
                  <>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Fee</dt>
                      <dd className="mt-1 text-sm text-gray-900">{formatCurrency(selectedPayout.fee || 0)}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Net Amount</dt>
                      <dd className="mt-1 text-sm text-gray-900">{formatCurrency(selectedPayout.net_amount || selectedPayout.amount)}</dd>
                    </div>
                  </>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedPayout.status)}`}>
                      {getStatusIcon(selectedPayout.status)}
                      {selectedPayout.status}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Mode</dt>
                  <dd className="mt-1 text-sm text-gray-900 uppercase">{selectedPayout.mode || 'UPI'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Beneficiary Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{selectedPayout.beneficiary_name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Beneficiary VPA/Account</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {selectedPayout.beneficiary_vpa || selectedPayout.beneficiary_account || 'N/A'}
                  </dd>
                </div>
                {selectedPayout.beneficiary_ifsc && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">IFSC Code</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedPayout.beneficiary_ifsc}</dd>
                  </div>
                )}
                {selectedPayout.gateway_payout_id && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Gateway Payout ID</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedPayout.gateway_payout_id}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDateTime(selectedPayout.created_at)}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDateTime(selectedPayout.updated_at)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payouts;
